import {FC} from "react";
import {useTranslation} from "react-i18next";
import {BoidGroup, byName, NamedBoidGroup} from "../../boids/model";

import {TabColumn, TabRow} from "../../../tab-content";
import {List, ListItem} from "@interstate/components/List";
import {Badge} from "@interstate/components/Badge";
import {Tooltip} from "@interstate/components/Tooltip";
import {useClientEntity} from "../../context";
import {supportedBoidAssignments} from "../../boids/transforms";
import {InformationCircleIcon} from "@interstate/components/Icons";
import {BoidAssignment} from "@common-core/coat-operational-hierarchy-appsync-model";
import {groupBoidAssignments} from "../helpers";

export const ClientHierarchyBoidsColumn: FC = () => {
  const {t} = useTranslation();
  const {entity} = useClientEntity();

  const toNamedGroup =
    (t: (key: string) => string) =>
    (group: BoidGroup<BoidAssignment[]>): NamedBoidGroup<BoidAssignment[]> => {
      return {
        name: t(`entity-detail.business-operation-types.${group.type}`),
        group
      };
    };

  return (
    <TabColumn
      name={"boids"}
      header={
        <hgroup className={"entity-detail-boid-column-header"}>
          <h3>{t("entity-detail.business-operations-ids")}</h3>
        </hgroup>
      }>
      <TabRow name={"client-hierarchy-boids"}>
        <List
          id={"client-hierarchy-boid-list"}
          data-testid={"client-hierarchy-boid-list"}
          className={"client-hierarchy-boid-list"}>
          {(entity.subaccounts || [])
            .map(su => {
              return {
                businessOperationType: su.subAccountType,
                businessOperationId: su.subAccountId,
                businessOperationStatus: su.subAccountStatus
              } as BoidAssignment;
            })
            .reduce(
              groupBoidAssignments,
              supportedBoidAssignments().map(group => ({
                type: group.type,
                boids: [] as BoidAssignment[]
              }))
            )
            .map(toNamedGroup(t))
            .sort(byName)
            .map(({name, group: {type, boids}}) => (
              <ListItem
                key={type}
                id={`${type}-boid-assignments-list-item`}
                data-testid={`${type}-boid-assignments-list-item`}
                className={"boid-assignments-list-item"}>
                <h4 className={"business-operation-type-name"}>{name}</h4>
                {boids.length > 0 ? (
                  <ul
                    id={`${type}-boid-badge-list`}
                    data-testid={`${type}-boid-badge-list`}
                    className={"boid-badge-list"}>
                    {boids
                      .sort((a: BoidAssignment, b: BoidAssignment) =>
                        a.businessOperationId.localeCompare(
                          b.businessOperationId
                        )
                      )
                      .map(boidAssignment => (
                        <li
                          key={`${type}-${boidAssignment.businessOperationId}`}
                          id={`${type}-${boidAssignment.businessOperationId}-boid-badge-item`}
                          data-testid={`${type}-${boidAssignment.businessOperationId}-boid-badge-item`}
                          className={"boid-badge-item"}>
                          {boidAssignment.businessOperationStatus ? (
                            <Tooltip
                              id={`boid-status-tooltip-${boidAssignment.businessOperationId}`}
                              data-testid={`boid-status-tooltip-${boidAssignment.businessOperationId}`}
                              size="small"
                              toolTipContent={
                                boidAssignment.businessOperationStatus
                              }
                              position="bottom">
                              <Badge
                                id={`boid-badge-${boidAssignment.businessOperationId}`}
                                data-testid={`boid-badge-${boidAssignment.businessOperationId}`}
                                variant={"transparent"}
                                endIcon={
                                  <InformationCircleIcon
                                    id={`boid-status-icon-${boidAssignment.businessOperationId}`}
                                    data-testid={`boid-status-icon-${boidAssignment.businessOperationId}`}
                                  />
                                }>
                                {boidAssignment.businessOperationId}
                              </Badge>
                            </Tooltip>
                          ) : (
                            <Badge variant={"transparent"}>
                              {boidAssignment.businessOperationId}
                            </Badge>
                          )}
                        </li>
                      ))}
                  </ul>
                ) : (
                  <span
                    className={"no-assignments"}
                    data-testid={`no-${type}-assignments`}>
                    {t("entity-detail.business-operation-types.no-assignments")}
                  </span>
                )}
              </ListItem>
            ))}
        </List>
      </TabRow>
    </TabColumn>
  );
};
